/**
 * Utility functions for accessible status indicators and error messages across themes
 */
import { getContrastRatio, getAccessibleBadgeColors } from './contrastUtils.js';

/**
 * Get accessible status indicator colors for both themes
 */
export const getStatusColors = () => {
  const badgeColors = getAccessibleBadgeColors();
  
  return {
    light: {
      // Success states
      success: {
        background: badgeColors.success.background, // green-700
        text: badgeColors.success.text, // white
        border: '#15803d', // green-700
        subtle: {
          background: '#dcfce7', // green-100
          text: '#166534', // green-800
          border: '#bbf7d0' // green-200
        }
      },
      
      // Error states
      error: {
        background: badgeColors.error.background, // red-600
        text: badgeColors.error.text, // white
        border: '#dc2626', // red-600
        subtle: {
          background: '#fee2e2', // red-100
          text: '#991b1b', // red-800
          border: '#fecaca' // red-200
        }
      },
      
      // Warning states
      warning: {
        background: '#b45309', // yellow-700 for better contrast
        text: '#ffffff', // white text for better contrast
        border: '#b45309', // yellow-700
        subtle: {
          background: '#fef3c7', // yellow-100
          text: '#92400e', // yellow-800
          border: '#fde68a' // yellow-200
        }
      },
      
      // Info states
      info: {
        background: badgeColors.info.background, // blue-600
        text: badgeColors.info.text, // white
        border: '#2563eb', // blue-600
        subtle: {
          background: '#dbeafe', // blue-100
          text: '#1e40af', // blue-800
          border: '#bfdbfe' // blue-200
        }
      },
      
      // Primary/Active states
      primary: {
        background: '#115e59', // teal-800 for better contrast on light
        text: '#ffffff', // white
        border: '#115e59', // teal-800
        subtle: {
          background: '#ccfbf1', // teal-100
          text: '#115e59', // teal-800
          border: '#99f6e4' // teal-200
        }
      },
      
      // Secondary/Inactive states
      secondary: {
        background: badgeColors.secondary.background, // gray-600
        text: badgeColors.secondary.text, // white
        border: '#4b5563', // gray-600
        subtle: {
          background: '#f3f4f6', // gray-100
          text: '#374151', // gray-700
          border: '#e5e7eb' // gray-200
        }
      }
    },
    
    dark: {
      // Success states
      success: {
        background: '#15803d', // green-700 (better contrast on dark)
        text: '#ffffff', // white
        border: '#15803d', // green-700
        subtle: {
          background: '#14532d', // green-900
          text: '#86efac', // green-300
          border: '#166534' // green-800
        }
      },
      
      // Error states
      error: {
        background: '#dc2626', // red-600
        text: '#ffffff', // white
        border: '#dc2626', // red-600
        subtle: {
          background: '#7f1d1d', // red-900
          text: '#fca5a5', // red-300
          border: '#991b1b' // red-800
        }
      },
      
      // Warning states
      warning: {
        background: '#f59e0b', // yellow-500 (better contrast on dark backgrounds)
        text: '#000000', // black text for better contrast
        border: '#f59e0b', // yellow-500
        subtle: {
          background: '#78350f', // yellow-900
          text: '#fcd34d', // yellow-300
          border: '#92400e' // yellow-800
        }
      },
      
      // Info states
      info: {
        background: '#2563eb', // blue-600
        text: '#ffffff', // white
        border: '#2563eb', // blue-600
        subtle: {
          background: '#1e3a8a', // blue-900
          text: '#93c5fd', // blue-300
          border: '#1e40af' // blue-800
        }
      },
      
      // Primary/Active states
      primary: {
        background: '#0f766e', // teal-700 for better contrast on dark
        text: '#ffffff', // white
        border: '#0f766e', // teal-700
        subtle: {
          background: '#134e4a', // teal-900
          text: '#5eead4', // teal-300
          border: '#115e59' // teal-800
        }
      },
      
      // Secondary/Inactive states
      secondary: {
        background: '#4b5563', // gray-600
        text: '#ffffff', // white
        border: '#4b5563', // gray-600
        subtle: {
          background: '#111827', // gray-900
          text: '#d1d5db', // gray-300
          border: '#374151' // gray-700
        }
      }
    }
  };
};

/**
 * Get theme-aware status classes for badges and indicators
 * @param {string} theme - Current theme ('light' or 'dark')
 * @param {string} status - Status type ('success', 'error', 'warning', 'info', 'primary', 'secondary')
 * @param {boolean} subtle - Whether to use subtle styling
 * @returns {string} Complete status class string
 */
export const getStatusClasses = (theme, status, subtle = false) => {
  const colors = getStatusColors();
  const statusColor = colors[theme]?.[status];
  
  if (!statusColor) {
    console.warn(`Unknown status type: ${status}`);
    return '';
  }
  
  const colorSet = subtle ? statusColor.subtle : statusColor;
  
  // Convert hex colors to Tailwind classes
  const bgClass = hexToTailwindBg(colorSet.background);
  const textClass = hexToTailwindText(colorSet.text);
  const borderClass = hexToTailwindBorder(colorSet.border);
  
  return `${bgClass} ${textClass} ${borderClass}`;
};

/**
 * Get accessible error message classes
 * @param {string} theme - Current theme
 * @param {boolean} subtle - Whether to use subtle styling
 * @returns {string} Error message classes
 */
export const getErrorClasses = (theme, subtle = false) => {
  return getStatusClasses(theme, 'error', subtle);
};

/**
 * Get accessible success message classes
 * @param {string} theme - Current theme
 * @param {boolean} subtle - Whether to use subtle styling
 * @returns {string} Success message classes
 */
export const getSuccessClasses = (theme, subtle = false) => {
  return getStatusClasses(theme, 'success', subtle);
};

/**
 * Get accessible warning message classes
 * @param {string} theme - Current theme
 * @param {boolean} subtle - Whether to use subtle styling
 * @returns {string} Warning message classes
 */
export const getWarningClasses = (theme, subtle = false) => {
  return getStatusClasses(theme, 'warning', subtle);
};

/**
 * Get accessible info message classes
 * @param {string} theme - Current theme
 * @param {boolean} subtle - Whether to use subtle styling
 * @returns {string} Info message classes
 */
export const getInfoClasses = (theme, subtle = false) => {
  return getStatusClasses(theme, 'info', subtle);
};

/**
 * Get subscription status badge classes
 * @param {string} theme - Current theme
 * @param {string} status - Subscription status ('active', 'trialing', 'canceled', 'past_due', etc.)
 * @returns {string} Badge classes
 */
export const getSubscriptionStatusClasses = (theme, status) => {
  const statusMap = {
    active: 'success',
    trialing: 'info',
    canceled: 'secondary',
    past_due: 'warning',
    unpaid: 'error',
    incomplete: 'warning',
    incomplete_expired: 'error'
  };
  
  const mappedStatus = statusMap[status] || 'secondary';
  return getStatusClasses(theme, mappedStatus, false);
};

/**
 * Get admin badge classes (for admin user indicators)
 * @param {string} theme - Current theme
 * @returns {string} Admin badge classes
 */
export const getAdminBadgeClasses = (theme) => {
  return getStatusClasses(theme, 'error', false); // Use error styling for admin badges
};

/**
 * Helper function to convert hex color to Tailwind background class
 * This is a simplified mapping - in a real app you might want a more comprehensive solution
 */
const hexToTailwindBg = (hex) => {
  const colorMap = {
    '#15803d': 'bg-green-700',
    '#dc2626': 'bg-red-600',
    '#b45309': 'bg-yellow-700',
    '#f59e0b': 'bg-yellow-500',
    '#2563eb': 'bg-blue-600',
    '#0f766e': 'bg-teal-700',
    '#4b5563': 'bg-gray-600',
    '#115e59': 'bg-teal-800',
    '#15803d': 'bg-green-700',
    '#0f766e': 'bg-teal-700',
    '#dcfce7': 'bg-green-100',
    '#fee2e2': 'bg-red-100',
    '#fef3c7': 'bg-yellow-100',
    '#dbeafe': 'bg-blue-100',
    '#ccfbf1': 'bg-teal-100',
    '#f3f4f6': 'bg-gray-100',
    '#14532d': 'bg-green-900',
    '#7f1d1d': 'bg-red-900',
    '#78350f': 'bg-yellow-900',
    '#1e3a8a': 'bg-blue-900',
    '#134e4a': 'bg-teal-900',
    '#111827': 'bg-gray-900',
    '#ffffff': 'bg-white'
  };
  
  return colorMap[hex] || 'bg-gray-500';
};

/**
 * Helper function to convert hex color to Tailwind text class
 */
const hexToTailwindText = (hex) => {
  const colorMap = {
    '#ffffff': 'text-white',
    '#000000': 'text-black',
    '#166534': 'text-green-800',
    '#991b1b': 'text-red-800',
    '#92400e': 'text-yellow-800',
    '#1e40af': 'text-blue-800',
    '#115e59': 'text-teal-800',
    '#374151': 'text-gray-700',
    '#86efac': 'text-green-300',
    '#fca5a5': 'text-red-300',
    '#fcd34d': 'text-yellow-300',
    '#93c5fd': 'text-blue-300',
    '#5eead4': 'text-teal-300',
    '#d1d5db': 'text-gray-300'
  };
  
  return colorMap[hex] || 'text-gray-900';
};

/**
 * Helper function to convert hex color to Tailwind border class
 */
const hexToTailwindBorder = (hex) => {
  const colorMap = {
    '#15803d': 'border-green-700',
    '#dc2626': 'border-red-600',
    '#b45309': 'border-yellow-700',
    '#f59e0b': 'border-yellow-500',
    '#2563eb': 'border-blue-600',
    '#0f766e': 'border-teal-700',
    '#4b5563': 'border-gray-600',
    '#115e59': 'border-teal-800',
    '#15803d': 'border-green-700',
    '#0f766e': 'border-teal-700',
    '#bbf7d0': 'border-green-200',
    '#fecaca': 'border-red-200',
    '#fde68a': 'border-yellow-200',
    '#bfdbfe': 'border-blue-200',
    '#99f6e4': 'border-teal-200',
    '#e5e7eb': 'border-gray-200',
    '#166534': 'border-green-800',
    '#991b1b': 'border-red-800',
    '#92400e': 'border-yellow-800',
    '#1e40af': 'border-blue-800',
    '#115e59': 'border-teal-800',
    '#374151': 'border-gray-700'
  };
  
  return colorMap[hex] || 'border-gray-500';
};

/**
 * Validate status indicator accessibility
 * @param {string} theme - Current theme
 * @param {string} status - Status type
 * @param {boolean} subtle - Whether using subtle styling
 * @returns {Object} Validation results
 */
export const validateStatusAccessibility = (theme, status, subtle = false) => {
  const colors = getStatusColors();
  const statusColor = colors[theme]?.[status];
  
  if (!statusColor) {
    return { valid: false, error: `Unknown status type: ${status}` };
  }
  
  const colorSet = subtle ? statusColor.subtle : statusColor;
  const contrastRatio = getContrastRatio(colorSet.text, colorSet.background);
  
  return {
    valid: contrastRatio >= 4.5,
    contrastRatio,
    colors: colorSet,
    meetsWCAGAA: contrastRatio >= 4.5,
    meetsWCAGAAA: contrastRatio >= 7
  };
};
